import { borderRadius, boxShadows, customSizes, palette, spaces, typography } from '@kimuno/theme';
import {
	Box,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	IconButton,
	SvgIcon,
	type SxProps,
	type Theme,
	Typography,
} from '@mui/material';
import type React from 'react';
import { useTranslation } from 'react-i18next';

import getMuiIcon from '../../../utils/getMuiIcon';
import useScreenSize from '../../../utils/useScreenSize';

export const AlertVariant = {
	TEXT: 'text',
	IMAGE: 'image',
	VIDEO: 'video',
} as const;

export type AlertVariantType = (typeof AlertVariant)[keyof typeof AlertVariant];

interface AlertDialogBaseProps {
	open: boolean;
	onClose: () => void;
	title: string;
	description: React.ReactNode | string;
	primaryButtonText: string;
	onPrimaryClick: () => void;
	secondaryButtonText?: string;
	onSecondaryClick?: () => void;
	customSx?: SxProps<Theme>;
}

interface AlertDialogTextProps extends AlertDialogBaseProps {
	variant: typeof AlertVariant.TEXT;
}

interface AlertDialogImageProps extends AlertDialogBaseProps {
	variant: typeof AlertVariant.IMAGE;
	imageSrc: string;
	imageAlt: string;
}

interface AlertDialogVideoProps extends AlertDialogBaseProps {
	variant: typeof AlertVariant.VIDEO;
	videoSrc: string;
}

type AlertDialogProps = AlertDialogTextProps | AlertDialogImageProps | AlertDialogVideoProps;

const AlertDialog = (props: AlertDialogProps) => {
	const {
		open,
		onClose,
		variant,
		title,
		description,
		primaryButtonText,
		secondaryButtonText,
		onPrimaryClick,
		onSecondaryClick,
		customSx,
	} = props;

	const { t } = useTranslation();
	const { isXsScreen } = useScreenSize();

	const paddingDialog = isXsScreen ? spaces.md : spaces.lg;

	return (
		<Dialog
			open={open}
			onClose={onClose}
			fullWidth
			maxWidth={false}
			sx={{
				'& .MuiDialog-paper': {
					width: '100%',
					maxWidth: isXsScreen
						? customSizes.dialog.maxWidthXs
						: variant === AlertVariant.VIDEO
							? customSizes.dialog.maxVideoWidth
							: customSizes.dialog.maxWidth,
					boxShadow: boxShadows.elevation6,
					margin: isXsScreen ? spaces.md : 0,
				},
				...customSx,
			}}
		>
			<DialogTitle
				sx={{
					display: 'flex',
					justifyContent: 'space-between',
					alignItems: 'center',
					padding: paddingDialog,
					gap: spaces.md,
					...(!isXsScreen && typography.h5),
				}}
			>
				{title}
				<IconButton onClick={onClose} size="small" type="button" aria-label={t('Close')}>
					<SvgIcon
						component={getMuiIcon('close')}
						sx={{
							color: palette.text.secondary,
						}}
						aria-hidden="true"
					/>
				</IconButton>
			</DialogTitle>
			<DialogContent sx={{ padding: paddingDialog, paddingBottomTop: 0 }}>
				{variant === AlertVariant.IMAGE && (
					<Box
						component="img"
						src={(props as AlertDialogImageProps).imageSrc}
						alt={(props as AlertDialogImageProps).imageAlt}
						sx={{
							width: '100%',
							borderRadius: borderRadius.sm,
							maxHeight: isXsScreen
								? customSizes.dialog.maxImageHeightXs
								: customSizes.dialog.maxImageHeight,
							objectFit: 'cover',
							marginBottom: isXsScreen ? spaces.xs : spaces.md,
						}}
					/>
				)}
				{variant === AlertVariant.VIDEO && (
					<Box
						sx={{
							marginBottom: isXsScreen ? spaces.xs : spaces.md,
							position: 'relative',
							aspectRatio: '16/9',
							'& iframe': {
								position: 'absolute',
								top: 0,
								left: 0,
								width: '100%',
								height: '100%',
								border: 0,
								borderRadius: borderRadius.sm,
							},
						}}
					>
						<iframe title="Video" src={(props as AlertDialogVideoProps).videoSrc} allowFullScreen />
					</Box>
				)}
				<Typography variant={isXsScreen ? 'body2' : 'body1'}>{description}</Typography>
			</DialogContent>
			<DialogActions
				sx={{
					display: 'flex',
					flexDirection: isXsScreen ? 'column-reverse' : 'row',
					gap: spaces.xs,
					padding: paddingDialog,
				}}
			>
				{secondaryButtonText && onSecondaryClick && (
					<Button
						variant="outlined"
						aria-label={secondaryButtonText}
						onClick={onSecondaryClick}
						size="large"
						type="button"
						sx={{
							flex: 1,
							width: '100%',
							borderColor: palette.primary.outlinedBorder,
							paddingX: spaces.lg,
						}}
					>
						<Typography
							sx={{
								color: palette.primary.main,
								...typography.button.alert,
							}}
						>
							{secondaryButtonText}
						</Typography>
					</Button>
				)}
				<Button
					variant="contained"
					onClick={onPrimaryClick}
					color="primary"
					size="large"
					type="button"
					sx={{
						flex: 1,
						width: '100%',
						marginLeft: '0px !important',
						boxShadow: boxShadows.elevation4,
						paddingX: spaces.lg,
					}}
				>
					<Typography
						sx={{
							color: palette.primary.contrast,
							...typography.button.alert,
						}}
					>
						{primaryButtonText}
					</Typography>
				</Button>
			</DialogActions>
		</Dialog>
	);
};

export default AlertDialog;
